import { ICreateRoomStateEvent } from 'matrix-js-sdk/src/matrix';

import { mergeRoomCreationPowerLevelOverrides } from 'hooks/roomCreation/useRoomCreationPowerLevels';
import { getWorkspaceMeetingPowerLevelOverrides } from 'hooks/useCreateMeeting/utils';
import { RoomPowerLevelOverridesOptions } from 'utils/matrixUtils';

export interface WorkspaceMeetingCreationParams {
  includeBot: boolean;
  powerLevelOverrides: RoomPowerLevelOverridesOptions;
  ownerPowerLevel: number;
}

export interface WorkspaceMeetingCreationContextInput {
  workspaceId: string | null | undefined;
  roomManagersInitialState: ICreateRoomStateEvent | null | undefined;
  teamRoomAccessInitialState: ICreateRoomStateEvent;
  meetingOwnerPowerLevel: number;
}

export interface WorkspaceMeetingCreationResult {
  managersStates: ICreateRoomStateEvent[];
  accessStates: ICreateRoomStateEvent[];
  workspaceCreationParams: Partial<WorkspaceMeetingCreationParams>;
}

/**
 * Assembles the workspace-context creation object + initial-state arrays shared by the three
 * meeting-space creators (`InstantCallModal`, `useScheduleMeetingSubmit`, `useCreateMessengerCall`).
 *
 * All three workspace-scoped outputs key off `workspaceId`, matching the `/messenger` contract in
 * `useMessengerRoomCreation` (`workspaceId && roomManagersInitialState`):
 * - `managersStates`, `accessStates`, and `workspaceCreationParams` are only populated inside a workspace.
 *
 * Pure function — no hooks. Callers resolve `roomManagersInitialState`, `teamRoomAccessInitialState`,
 * and `meetingOwnerPowerLevel` from their own hook bodies and feed them in.
 */
export const buildWorkspaceMeetingCreationContext = ({
  workspaceId,
  roomManagersInitialState,
  teamRoomAccessInitialState,
  meetingOwnerPowerLevel,
}: WorkspaceMeetingCreationContextInput): WorkspaceMeetingCreationResult => {
  const managersStates: ICreateRoomStateEvent[] =
    workspaceId && roomManagersInitialState ? [roomManagersInitialState] : [];
  const accessStates: ICreateRoomStateEvent[] = workspaceId ? [teamRoomAccessInitialState] : [];
  const workspaceCreationParams: Partial<WorkspaceMeetingCreationParams> = workspaceId
    ? {
        includeBot: true,
        powerLevelOverrides: mergeRoomCreationPowerLevelOverrides(getWorkspaceMeetingPowerLevelOverrides()),
        ownerPowerLevel: meetingOwnerPowerLevel,
      }
    : {};

  return { managersStates, accessStates, workspaceCreationParams };
};
